import { useCallback, useState } from "react";

interface UnsavedGuardOptions {
	isDirty: boolean;
	save: () => Promise<void>;
	reset: () => void;
}

/**
 * Wraps anything that would navigate away from a dirty form — closing the drawer,
 * the breadcrumb, opening a subtask — and holds the intent until the user has
 * decided what to do with the edits, so "Save" resumes where they were going
 * instead of just closing.
 */
export function useUnsavedGuard({ isDirty, save, reset }: UnsavedGuardOptions) {
	const [pending, setPending] = useState<(() => void) | null>(null);

	const guard = useCallback(
		(action: () => void) => {
			if (isDirty) {
				setPending(() => action);

				return;
			}

			action();
		},
		[isDirty]
	);

	const keepEditing = useCallback(() => setPending(null), []);

	const discard = useCallback(() => {
		const action = pending;

		setPending(null);
		reset();
		action?.();
	}, [pending, reset]);

	const saveAndContinue = useCallback(async () => {
		const action = pending;

		await save();
		setPending(null);
		action?.();
	}, [pending, save]);

	return { guard, isPrompting: !!pending, keepEditing, discard, saveAndContinue };
}
